// src/pages/Matriculas/components/ResumenCompra.jsx
import React from 'react';
import { Card, Descriptions, Button, Space, Alert, Divider } from 'antd';
import { 
  ShoppingCartOutlined,
  UserOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

export default function ResumenCompra({ user, recinto, configuracion, loading, onConfirmar, onVolver }) {
  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(monto || 0);
  };

  const calcularFechaVencimiento = () => {
    const hoy = dayjs();
    
    if (configuracion.tipoVigencia === 'anual') {
      return hoy.add(1, 'year').format('DD/MM/YYYY');
    } else if (configuracion.tipoVigencia === 'meses' && configuracion.mesesVigencia) {
      return hoy.add(configuracion.mesesVigencia, 'month').format('DD/MM/YYYY');
    }
    
    return 'Sin vencimiento';
  };

  const getVigenciaTexto = () => {
    if (configuracion.tipoVigencia === 'anual') {
      return '1 año';
    } else if (configuracion.tipoVigencia === 'meses' && configuracion.mesesVigencia) {
      return `${configuracion.mesesVigencia} meses`;
    }
    return 'Indefinida';
  };

  return (
    <div className="space-y-6">
      <Alert
        message="Revisa la información antes de continuar"
        description="Una vez agregada al carrito, podrás proceder con el pago de la matrícula."
        type="info"
        showIcon
        icon={<CheckCircleOutlined />}
      />

      {/* Datos del usuario */}
      <Card title={
        <span>
          <UserOutlined className="mr-2" />
          Datos del beneficiario
        </span>
      }>
        <Descriptions column={1}>
          <Descriptions.Item label="Nombre">{user.name}</Descriptions.Item>
          <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
          {user.rut && <Descriptions.Item label="RUT">{user.rut}</Descriptions.Item>}
          {user.phone && <Descriptions.Item label="Teléfono">{user.phone}</Descriptions.Item>}
        </Descriptions>
      </Card>

      {/* Detalles de la matrícula */}
      <Card title={
        <span>
          <EnvironmentOutlined className="mr-2" />
          Matrícula a adquirir
        </span>
      }>
        <Descriptions column={1}>
          <Descriptions.Item label="Recinto">
            <strong>{recinto.nombre}</strong>
          </Descriptions.Item>
          {recinto.direccion && (
            <Descriptions.Item label="Dirección">{recinto.direccion}</Descriptions.Item>
          )}
          <Descriptions.Item label="Vigencia">
            <span className="text-blue-600 font-semibold">{getVigenciaTexto()}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Válida hasta">
            <span className="font-semibold">{calcularFechaVencimiento()}</span>
          </Descriptions.Item>
        </Descriptions>

        {configuracion.descripcion && (
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <p className="text-sm text-gray-700">{configuracion.descripcion}</p>
          </div>
        )}
      </Card>

      {/* Resumen de pago */}
      <Card title={
        <span>
          <DollarOutlined className="mr-2" />
          Resumen de pago
        </span>
      }>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-700">Matrícula {recinto.nombre}</span>
            <span className="font-semibold">{formatearMoneda(configuracion.valorMatricula)}</span>
          </div>

          <Divider className="my-2" />

          <div className="flex justify-between items-center py-3 bg-blue-50 px-4 rounded-lg">
            <span className="text-lg font-bold text-blue-900">Total a pagar</span>
            <span className="text-2xl font-bold text-blue-600">
              {formatearMoneda(configuracion.valorMatricula)}
            </span>
          </div>

          <Alert
            message="El pago se procesará en el carrito"
            description="Una vez agregada la matrícula al carrito, podrás proceder con el pago usando los métodos disponibles."
            type="warning"
            showIcon
          />
        </div>
      </Card>

      {/* Acciones */}
      <Card>
        <Space className="w-full justify-between">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onVolver}
            size="large"
            disabled={loading}
          >
            Volver
          </Button>

          <Button
            type="primary"
            icon={<ShoppingCartOutlined />}
            onClick={onConfirmar}
            size="large"
            loading={loading}
          >
            {loading ? 'Agregando...' : 'Agregar al Carrito'}
          </Button>
        </Space>
      </Card>
    </div>
  );
}
